
import React from 'react';
import { useDoctorDashboard } from '@/hooks/useDoctorDashboard';
import { useDashboardActivities } from '@/hooks/useDashboardActivities';
import DoctorHeader from '@/components/doctor/DoctorHeader';
import DoctorFooterNav from '@/components/doctor/DoctorFooterNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, FileText, Megaphone, Users, CheckCircle } from 'lucide-react';

// Import our custom components
import ActivityChart from '@/components/doctor/ActivityChart';
import QuickActions from '@/components/doctor/QuickActions';
import SmartInsights from '@/components/doctor/SmartInsights';
import DashboardPatientList from '@/components/doctor/DashboardPatientList';
import DoctorAdStats from '@/components/doctor/DoctorAdStats';
import NotificationList from '@/components/doctor/NotificationList';

const DoctorDashboard: React.FC = () => {
  const { doctorDashboardData, isLoading } = useDoctorDashboard();
  const { 
    upcomingAppointments, 
    completedTreatments, 
    newCasesThisWeek,
    acceptedPatients,
    hasActivities 
  } = useDashboardActivities();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DoctorHeader />
      
      <div className="container mx-auto p-4 pt-20 pb-24">
        <h1 className="text-2xl font-bold mb-6">Dashboard</h1>
        
        {/* Key Metrics Section */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <FileText className="h-6 w-6 text-primary mb-2" />
                <div className="text-2xl font-bold">{newCasesThisWeek}</div>
                <p className="text-xs text-muted-foreground">New Cases (Week)</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Users className="h-6 w-6 text-indigo-500 mb-2" />
                <div className="text-2xl font-bold">{acceptedPatients}</div>
                <p className="text-xs text-muted-foreground">Accepted Patients</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Calendar className="h-6 w-6 text-blue-500 mb-2" />
                <div className="text-2xl font-bold">{upcomingAppointments.length}</div>
                <p className="text-xs text-muted-foreground">Upcoming (24h)</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="h-6 w-6 text-green-500 mb-2" />
                <div className="text-2xl font-bold">{completedTreatments}</div>
                <p className="text-xs text-muted-foreground">Completed (Month)</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col items-center text-center">
                <Megaphone className="h-6 w-6 text-amber-500 mb-2" />
                <div className="text-2xl font-bold">{doctorDashboardData.adStats?.activeAds || 0}</div>
                <p className="text-xs text-muted-foreground">Active Ads</p>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Activity Chart & Quick Actions Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            <ActivityChart />
          </div>
          
          <div className="lg:col-span-1">
            <QuickActions />
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Tabs defaultValue="new-cases" className="w-full">
              <TabsList className="mb-4">
                <TabsTrigger value="new-cases">New Cases</TabsTrigger>
                <TabsTrigger value="accepted">Accepted Patients</TabsTrigger>
                <TabsTrigger value="follow-ups">Follow-ups</TabsTrigger>
              </TabsList>
              
              <TabsContent value="new-cases">
                <Card>
                  <CardHeader>
                    <CardTitle>New Case Submissions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DashboardPatientList patients={doctorDashboardData.newCases} type="new" />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="accepted">
                <Card>
                  <CardHeader>
                    <CardTitle>Accepted Patients</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DashboardPatientList patients={doctorDashboardData.acceptedPatients} type="pending" />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="follow-ups">
                <Card>
                  <CardHeader>
                    <CardTitle>Upcoming Follow-ups</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DashboardPatientList 
                      patients={doctorDashboardData.acceptedPatients.filter(p => 
                        p.status === 'follow-up' || 
                        (p.progress && p.progress >= 90)
                      )} 
                      type="followup" 
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
          
          <div className="space-y-6">
            {/* Smart Insights Card */}
            <SmartInsights patients={[...doctorDashboardData.newCases, ...doctorDashboardData.acceptedPatients]} />
            
            {/* Ad Performance Card */}
            <Card>
              <CardHeader>
                <CardTitle>Your Ad Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <DoctorAdStats adStats={doctorDashboardData.adStats} />
              </CardContent>
            </Card>
            
            {/* Recent Notifications */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Notifications</CardTitle>
              </CardHeader>
              <CardContent>
                <NotificationList notifications={doctorDashboardData.notifications.slice(0, 5)} />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      
      <DoctorFooterNav />
    </div>
  );
};

export default DoctorDashboard;
